import { delay, API_ENDPOINTS, apiClient, ApiResponse, USE_MOCK } from './client';
import { PricingRule, AvailabilityRule, Event } from '@/types/revenue-manager';

export interface CalendarDay {
  date: string;
  price: number;
  basePrice: number;
  percentChange: number;
  occupancy: number;
  type: 'standard' | 'high-demand' | 'discount' | 'event';
  eventName?: string;
  isToday?: boolean;
  pricingRulesCount?: number;
  availabilityRulesCount?: number;
  eventsCount?: number;
  restrictions?: string[];
}

export interface CalendarSummary {
  averagePrice: number;
  averagePriceChange: number;
  peakPrice: number;
  peakPriceNote: string;
  lowestPrice: number;
  lowestPriceNote: string;
}

export interface CalendarData {
  summary: CalendarSummary;
  days: CalendarDay[];
}

// Generate mock calendar data for a given month
const generateMockCalendarData = (propertyId: string, year: number, month: number): CalendarData => {
  const basePrice = propertyId === 'prop-1' ? 150 : propertyId === 'prop-2' ? 200 : 175;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  
  const days: CalendarDay[] = [];
  let totalPrice = 0;
  let peakPrice = 0;
  let lowestPrice = Infinity;
  
  // Event days (15th and 22nd for demo)
  const eventDays = [15, 22];
  
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const isEventDay = eventDays.includes(day);
    const isToday = date.toDateString() === today.toDateString();
    
    // Determine day type and price adjustment
    let type: CalendarDay['type'] = 'standard';
    let priceMultiplier = 1;
    let eventName: string | undefined;
    
    if (isEventDay) {
      type = 'event';
      priceMultiplier = 1.5 + Math.random() * 0.3;
      eventName = day === 15 ? 'Tech Conference' : 'Music Festival';
    } else if (isWeekend) {
      type = 'high-demand';
      priceMultiplier = 1.3 + Math.random() * 0.2;
    } else if (day % 7 === 3) {
      type = 'discount';
      priceMultiplier = 0.85 + Math.random() * 0.1;
    } else {
      type = 'standard';
      priceMultiplier = 1 + Math.random() * 0.2;
    }
    
    const price = Math.round(basePrice * priceMultiplier);
    const percentChange = Math.round(((price - basePrice) / basePrice) * 100);
    const occupancy = Math.round(60 + Math.random() * 35);
    
    totalPrice += price;
    if (price > peakPrice) peakPrice = price;
    if (price < lowestPrice) lowestPrice = price;
    
    days.push({
      date: date.toISOString().split('T')[0],
      price,
      basePrice,
      percentChange,
      occupancy,
      type,
      eventName,
      isToday,
    });
  }
  
  const averagePrice = Math.round(totalPrice / daysInMonth);
  const lastMonthAvg = averagePrice * 0.9;
  const averagePriceChange = Math.round(((averagePrice - lastMonthAvg) / lastMonthAvg) * 100);
  
  return {
    summary: {
      averagePrice,
      averagePriceChange,
      peakPrice,
      peakPriceNote: 'Highest rate this month',
      lowestPrice,
      lowestPriceNote: 'Best value days',
    },
    days,
  };
};

// In-memory cache for mock data to persist edits during session
const mockDataCache: Record<string, CalendarData> = {};

const getCacheKey = (propertyId: string, year: number, month: number) => 
  `${propertyId}-${year}-${month}`;

const getOrCreateMockData = (propertyId: string, year: number, month: number): CalendarData => {
  const key = getCacheKey(propertyId, year, month);
  if (!mockDataCache[key]) {
    mockDataCache[key] = generateMockCalendarData(propertyId, year, month);
  }
  return mockDataCache[key];
};

export async function fetchCalendarData(
  propertyId: string,
  year: number,
  month: number
): Promise<ApiResponse<CalendarData>> {
  if (USE_MOCK) {
    await delay(300);
    return {
      success: true,
      data: getOrCreateMockData(propertyId, year, month),
    };
  }
  
  const response = await apiClient.get<ApiResponse<CalendarData>>(
    API_ENDPOINTS.calendar.getData(propertyId, year, month)
  );
  return response.data;
}

export interface UpdateCalendarDayInput {
  propertyId: string;
  date: string;
  basePrice: number;
  percentChange: number;
}

// Helper to check if a rule is active on a given date
const isRuleActiveOnDate = (rule: PricingRule | AvailabilityRule, dateStr: string): boolean => {
  const date = new Date(dateStr);
  const dayOfWeek = date.getDay();
  
  // Check execution days
  if (rule.executionDays && rule.executionDays.length > 0) {
    const executionDates = rule.executionDays.map(d => new Date(d).toISOString().split('T')[0]);
    if (!executionDates.includes(dateStr)) return false;
  }
  
  // Check day of week
  if (rule.daysOfWeek && rule.daysOfWeek.length > 0) {
    if (!rule.daysOfWeek.includes(dayOfWeek)) return false;
  }
  
  // Check date range
  if (rule.startDate) {
    const startDate = new Date(rule.startDate);
    startDate.setHours(0, 0, 0, 0);
    if (date < startDate) return false;
  }
  if (rule.endDate) {
    const endDate = new Date(rule.endDate);
    endDate.setHours(23, 59, 59, 999);
    if (date > endDate) return false;
  }
  
  return rule.isActive;
};

// Helper to check if an event is active on a given date
const isEventActiveOnDate = (event: Event, dateStr: string): boolean => {
  const date = new Date(dateStr);
  const startDate = new Date(event.startDate);
  const endDate = new Date(event.endDate);
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(23, 59, 59, 999);
  return date >= startDate && date <= endDate;
};

// Enrich calendar data with pricing rules, availability rules, and events
export const enrichCalendarWithRules = (
  calendarData: CalendarData,
  basePrice: number,
  pricingRules: PricingRule[] = [],
  availabilityRules: AvailabilityRule[] = [],
  events: Event[] = []
): CalendarData => {
  const enrichedDays = calendarData.days.map(day => {
    const dateStr = day.date;
    
    // Get active rules and events for this day
    const activePricingRules = pricingRules.filter(rule => isRuleActiveOnDate(rule, dateStr));
    const activeAvailabilityRules = availabilityRules.filter(rule => isRuleActiveOnDate(rule, dateStr));
    const activeEvents = events.filter(event => isEventActiveOnDate(event, dateStr));
    
    // Calculate price based on pricing rules
    let calculatedPrice = basePrice;
    let totalAdjustment = 0;
    
    // Apply pricing rules
    activePricingRules.forEach(rule => {
      if (rule.adjustmentType === 'percentage') {
        totalAdjustment += rule.adjustmentValue;
      } else if (rule.adjustmentType === 'fixed') {
        calculatedPrice += rule.adjustmentValue;
      }
    });
    
    // Apply percentage adjustment
    if (totalAdjustment !== 0) {
      calculatedPrice = calculatedPrice * (1 + totalAdjustment / 100);
    }
    
    calculatedPrice = Math.round(calculatedPrice);
    const percentChange = Math.round(((calculatedPrice - basePrice) / basePrice) * 100);
    
    // Determine day type
    let type: CalendarDay['type'] = 'standard';
    let eventName: string | undefined;
    
    if (activeEvents.length > 0) {
      type = 'event';
      eventName = activeEvents.map(e => e.name).join(', ');
    } else if (activePricingRules.length > 0) {
      const hasIncrease = activePricingRules.some(r => 
        (r.adjustmentType === 'percentage' && r.adjustmentValue > 0) ||
        (r.adjustmentType === 'fixed' && r.adjustmentValue > 0)
      );
      const hasDecrease = activePricingRules.some(r => 
        (r.adjustmentType === 'percentage' && r.adjustmentValue < 0) ||
        (r.adjustmentType === 'fixed' && r.adjustmentValue < 0)
      );
      
      if (hasIncrease) {
        type = 'high-demand';
      } else if (hasDecrease) {
        type = 'discount';
      }
    }
    
    // Get restrictions from availability rules
    const restrictions: string[] = [];
    activeAvailabilityRules.forEach(rule => {
      if (rule.actionType === 'setMinStay' && typeof rule.actionValue === 'number') {
        restrictions.push(`Min ${rule.actionValue}N`);
      } else if (rule.actionType === 'setMaxStay' && typeof rule.actionValue === 'number') {
        restrictions.push(`Max ${rule.actionValue}N`);
      } else if (rule.actionType === 'setStatus') {
        if (rule.actionValue === 'stop_sell') restrictions.push('Stop Sell');
        if (rule.actionValue === 'closed_to_arrival') restrictions.push('CTA');
        if (rule.actionValue === 'closed_to_departure') restrictions.push('CTD');
      }
    });
    
    return {
      ...day,
      price: calculatedPrice,
      basePrice,
      percentChange,
      type,
      eventName,
      pricingRulesCount: activePricingRules.length,
      availabilityRulesCount: activeAvailabilityRules.length,
      eventsCount: activeEvents.length,
      restrictions: restrictions.length > 0 ? restrictions : undefined,
    };
  });
  
  // Recalculate summary
  const totalPrice = enrichedDays.reduce((sum, d) => sum + d.price, 0);
  const averagePrice = Math.round(totalPrice / enrichedDays.length);
  const peakPrice = Math.max(...enrichedDays.map(d => d.price));
  const lowestPrice = Math.min(...enrichedDays.map(d => d.price));
  
  return {
    summary: {
      averagePrice,
      averagePriceChange: calendarData.summary.averagePriceChange,
      peakPrice,
      peakPriceNote: 'Highest rate this month',
      lowestPrice,
      lowestPriceNote: 'Best value days',
    },
    days: enrichedDays,
  };
};

export async function updateCalendarDay(
  input: UpdateCalendarDayInput
): Promise<ApiResponse<CalendarDay>> {
  if (USE_MOCK) {
    await delay(200);
    
    const date = new Date(input.date);
    const year = date.getFullYear();
    const month = date.getMonth();
    const data = getOrCreateMockData(input.propertyId, year, month);
    
    const dayIndex = data.days.findIndex(d => d.date === input.date);
    if (dayIndex === -1) {
      return { success: false, data: null as unknown as CalendarDay, error: 'Day not found' };
    }
    
    const newPrice = Math.round(input.basePrice * (1 + input.percentChange / 100));
    
    data.days[dayIndex] = {
      ...data.days[dayIndex],
      basePrice: input.basePrice,
      percentChange: input.percentChange,
      price: newPrice,
    };
    
    // Recalculate summary
    const totalPrice = data.days.reduce((sum, d) => sum + d.price, 0);
    data.summary.averagePrice = Math.round(totalPrice / data.days.length);
    data.summary.peakPrice = Math.max(...data.days.map(d => d.price));
    data.summary.lowestPrice = Math.min(...data.days.map(d => d.price));
    
    return { success: true, data: data.days[dayIndex] };
  }
  
  const response = await apiClient.patch<ApiResponse<CalendarDay>>(
    API_ENDPOINTS.calendar.updateDate(input.propertyId, input.date),
    { basePrice: input.basePrice, percentChange: input.percentChange }
  );
  return response.data;
}
